// lib/timeline-generation-placeholders.ts
import { STUDIO_MODEL_REGISTRY, type StudioModelId } from '#lib/model-registry';

// Permissive structural shape of a generation task, satisfied by GenerationTask
// and by the looser task rows the timeline signature is built from.
export interface PendingVideoGenerationTaskSource {
  id?: string | null;
  workspaceId?: string | null;
  type?: string | null;
  mergeStrategy?: string | null;
  layer?: unknown;
  error?: string | null;
  isOutcomeReported?: boolean | null;
  layerOverride?: unknown;
  videoDurationSeconds?: number | string | null;
  params?: {
    duration?: unknown;
    prompt?: unknown;
  } | null;
  model?: string | null;
  targetWidth?: number | string | null;
  targetHeight?: number | string | null;
}

export interface PendingVideoGenerationTrack {
  taskId: string;
  label: string;
  model: string;
  startMs: number;
  endMs: number;
  durationMs: number;
  outputWidth: number | null;
  outputHeight: number | null;
}

const FALLBACK_VIDEO_GENERATION_DURATION_MS = 5000;

const toFiniteNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const readTaskDurationMs = (task: PendingVideoGenerationTaskSource): number => {
  const explicitVideoDurationSeconds = toFiniteNumber(task.videoDurationSeconds);
  if (explicitVideoDurationSeconds !== null && explicitVideoDurationSeconds > 0) {
    return Math.max(1, Math.round(explicitVideoDurationSeconds * 1000));
  }

  const paramsDurationSeconds = toFiniteNumber(task.params?.duration);
  if (paramsDurationSeconds !== null && paramsDurationSeconds > 0) {
    return Math.max(1, Math.round(paramsDurationSeconds * 1000));
  }

  return FALLBACK_VIDEO_GENERATION_DURATION_MS;
};

export const resolvePendingTrackModelName = (modelId: string): string => {
  const normalizedModelId = modelId.trim();
  if (!normalizedModelId) return 'video';
  return STUDIO_MODEL_REGISTRY[normalizedModelId as StudioModelId]?.name ?? normalizedModelId;
};

const isPendingVideoGenerationTask = (task: PendingVideoGenerationTaskSource): boolean => (
  task.type === 'film' &&
  task.mergeStrategy === 'addInList' &&
  !task.layer &&
  !task.error &&
  task.isOutcomeReported !== true
);

export const resolvePendingVideoGenerationTrack = (
  task: PendingVideoGenerationTaskSource
): PendingVideoGenerationTrack | null => {
  if (!isPendingVideoGenerationTask(task)) return null;

  const layerOverride = (typeof task.layerOverride === 'object' && task.layerOverride !== null
    ? task.layerOverride
    : {}) as { timelineStartMs?: unknown; timelineEndMs?: unknown };
  const startMs = Math.max(0, Math.round(toFiniteNumber(layerOverride.timelineStartMs) ?? 0));
  const explicitEndMs = toFiniteNumber(layerOverride.timelineEndMs);
  const fallbackDurationMs = readTaskDurationMs(task);
  const endMs =
    explicitEndMs !== null && explicitEndMs > startMs
      ? Math.round(explicitEndMs)
      : startMs + fallbackDurationMs;
  const durationMs = Math.max(1, endMs - startMs);
  const model = typeof task.model === 'string' && task.model.trim().length > 0
    ? task.model.trim()
    : 'video';
  const modelName = resolvePendingTrackModelName(model);
  const outputWidth = toFiniteNumber(task.targetWidth);
  const outputHeight = toFiniteNumber(task.targetHeight);

  return {
    taskId: typeof task.id === 'string' ? task.id.trim() : '',
    label: `generating ${modelName}`,
    model,
    startMs,
    endMs: startMs + durationMs,
    durationMs,
    outputWidth: outputWidth !== null ? Math.round(outputWidth) : null,
    outputHeight: outputHeight !== null ? Math.round(outputHeight) : null,
  };
};
